import React, { useState, useEffect } from 'react';
import { XCircle, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Tour {
  id: string;
  name: string;
  departure: string;
  group_tour_price: number;
  reduced_group_price: number;
  private_price: number;
}

interface CreateBookingModalProps {
  tours: Tour[];
  onClose: () => void;
  onSuccess: () => void;
}

const CreateBookingModal: React.FC<CreateBookingModalProps> = ({ tours, onClose, onSuccess }) => {
  const generateBookingCode = () => `SF-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;

  const [formData, setFormData] = useState({
    tour_id: '',
    booking_date: '',
    start_time: '',
    group_type: 'group' as 'group' | 'reduced' | 'private',
    persons: 2,
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    pickup_address: 'Las Vegas Restaurant in Sierpe',
    total_price: 0,
    payment_method: 'cash',
    status: 'confirmed' as 'not_confirmed' | 'confirmed' | 'cancelled',
    notes: '',
    booking_code: generateBookingCode(),
    boot_sizes: [] as string[]
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bootSizeInput, setBootSizeInput] = useState('');

  const selectedTour = tours.find(t => t.id === formData.tour_id);

  const calculatePayPalCommission = (amount: number): number => {
    if (amount >= 1 && amount <= 50.99) return 4;
    if (amount >= 51 && amount <= 100.99) return 6;
    if (amount >= 101 && amount <= 150.99) return 7;
    if (amount >= 151 && amount <= 200.99) return 8;
    if (amount >= 201 && amount <= 250.99) return 9;
    if (amount >= 251 && amount <= 300.99) return 10;
    if (amount >= 301 && amount <= 350.99) return 11;
    if (amount >= 351 && amount <= 400.99) return 12;
    if (amount >= 401 && amount <= 500.99) return 14;
    if (amount >= 501 && amount <= 1000.99) return 24;
    if (amount >= 1001 && amount <= 1500.99) return 34;
    return 34;
  };

  const calculatePrice = () => {
    if (!selectedTour) return 0;

    let basePrice = 0;
    switch (formData.group_type) {
      case 'private':
        basePrice = selectedTour.private_price || 0;
        break;
      case 'reduced':
        basePrice = selectedTour.reduced_group_price || 0;
        break;
      default:
        basePrice = selectedTour.group_tour_price || 0;
    }

    const subtotal = basePrice * formData.persons;
    const commission = formData.payment_method === 'paypal' ? calculatePayPalCommission(subtotal) : 0;
    return subtotal + commission;
  };

  const getSubtotal = () => {
    if (!selectedTour) return 0;
    let basePrice = 0;
    switch (formData.group_type) {
      case 'private':
        basePrice = selectedTour.private_price || 0;
        break;
      case 'reduced':
        basePrice = selectedTour.reduced_group_price || 0;
        break;
      default:
        basePrice = selectedTour.group_tour_price || 0;
    }
    return basePrice * formData.persons;
  };

  const addBootSize = () => {
    if (bootSizeInput.trim()) {
      setFormData(prev => ({
        ...prev,
        boot_sizes: [...prev.boot_sizes, bootSizeInput.trim()]
      }));
      setBootSizeInput('');
    }
  };

  const removeBootSize = (index: number) => {
    setFormData(prev => ({
      ...prev,
      boot_sizes: prev.boot_sizes.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const totalPrice = calculatePrice();

      const { error: insertError } = await supabase
        .from('bookings')
        .insert({
          tour_id: formData.tour_id,
          booking_date: formData.booking_date,
          start_time: formData.start_time || selectedTour?.departure,
          group_type: formData.group_type,
          persons: formData.persons,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone || '',
          pickup_address: formData.pickup_address || '',
          total_price: totalPrice,
          payment_method: formData.payment_method,
          booking_code: formData.booking_code,
          status: formData.status,
          notes: formData.notes || null,
          boot_sizes: formData.boot_sizes
        });

      if (insertError) throw insertError;

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating booking:', err);
      setError('Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTourChange = (tourId: string) => {
    const tour = tours.find(t => t.id === tourId);
    setFormData(prev => ({
      ...prev,
      tour_id: tourId,
      start_time: tour?.departure || ''
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-[#2A382A]">Create New Booking</h2>
            <button
              onClick={onClose}
              type="button"
              className="text-gray-500 hover:text-gray-700"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="p-6 overflow-y-auto flex-1">
            {error && (
              <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg text-base">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Booking Code *
                </label>
                <input
                  type="text"
                  required
                  value={formData.booking_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, booking_code: e.target.value.toUpperCase() }))}
                  className="w-full px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                  placeholder="SF-XXXXXXXX"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Tour *
                </label>
                <select
                  required
                  value={formData.tour_id}
                  onChange={(e) => handleTourChange(e.target.value)}
                  className="w-full px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                >
                  <option value="">Select a tour</option>
                  {tours.map(tour => (
                    <option key={tour.id} value={tour.id}>{tour.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Booking Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.booking_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, booking_date: e.target.value }))}
                  className="w-full px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Start Time *
                </label>
                <input
                  type="time"
                  required
                  value={formData.start_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                  className="w-full px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Group Type *
                </label>
                <select
                  required
                  value={formData.group_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, group_type: e.target.value as any }))}
                  className="w-full px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                >
                  <option value="group">Group Tour</option>
                  <option value="reduced">Reduced Group</option>
                  <option value="private">Private Tour</option>
                </select>
              </div>

              <div>
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Number of Persons *
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  value={formData.persons}
                  onChange={(e) => setFormData(prev => ({ ...prev, persons: parseInt(e.target.value) }))}
                  className="w-full px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  className="w-full px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  className="w-full px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                  placeholder="Optional"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Pickup Address
                </label>
                <input
                  type="text"
                  value={formData.pickup_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, pickup_address: e.target.value }))}
                  className="w-full px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                  placeholder="Optional - Default: Las Vegas Restaurant in Sierpe"
                />
              </div>

              <div>
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Payment Method *
                </label>
                <select
                  required
                  value={formData.payment_method}
                  onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                  className="w-full px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                >
                  <option value="cash">Cash</option>
                  <option value="paypal">PayPal</option>
                  <option value="card">Card</option>
                  <option value="sinpe">SINPE Móvil</option>
                  <option value="transfer">Bank Transfer</option>
                </select>
              </div>

              <div>
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Status *
                </label>
                <select
                  required
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                  className="w-full px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                >
                  <option value="not_confirmed">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Boot Sizes
                </label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={bootSizeInput}
                      onChange={(e) => setBootSizeInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBootSize())}
                      className="flex-1 px-4 py-3 text-base text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent"
                      placeholder="Enter boot size (e.g., US 9, EU 42)"
                    />
                    <button
                      type="button"
                      onClick={addBootSize}
                      className="px-4 py-3 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  {formData.boot_sizes.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.boot_sizes.map((size, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg text-base"
                        >
                          <span>{size}</span>
                          <button
                            type="button"
                            onClick={() => removeBootSize(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-base font-medium text-[#2A382A] mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E] focus:border-transparent resize-none"
                  placeholder="Optional notes about the booking"
                />
              </div>

              {selectedTour && (
                <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex justify-between items-center text-base">
                    <span className="text-[#2A382A]/70">Subtotal:</span>
                    <span className="font-medium text-[#2A382A]">${getSubtotal().toFixed(2)}</span>
                  </div>
                  {formData.payment_method === 'paypal' && (
                    <div className="flex justify-between items-center text-base">
                      <span className="text-[#2A382A]/70">PayPal Commission:</span>
                      <span className="font-medium text-[#2A382A]">
                        ${calculatePayPalCommission(getSubtotal()).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                    <span className="text-lg font-medium text-[#2A382A]">Total Price:</span>
                    <span className="text-2xl font-bold text-[#953B0E]">
                      ${calculatePrice().toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 p-6 border-t border-gray-200 flex-shrink-0">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 text-base bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? 'Creating...' : 'Create Booking'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-base bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBookingModal;
